import { Camera, Loader2 } from "lucide-react";
import { useRef } from "react";

import { useUploadBirdPhoto } from "@/lib/birds";

/**
 * The camera button on a bird's photo.
 *
 * `accept="image/*"` with no `capture` attribute is deliberate: on a phone it offers the
 * camera *and* the library, and the breeder picks. `capture` would force the camera, which
 * is wrong for a photo taken yesterday in the birdroom.
 */
export function BirdPhotoButton({
  birdId,
  hasPhoto,
  onError,
}: {
  birdId: string;
  hasPhoto: boolean;
  onError: (message: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useUploadBirdPhoto(birdId);
  const label = hasPhoto ? "Change photo" : "Add photo";

  async function handleChange(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];

    // Cleared straight away so choosing the same file again still fires `change`.
    event.target.value = "";

    if (!file) {
      return;
    }

    onError(null);

    try {
      await upload.mutateAsync(file);
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : "Could not upload the photo.");
    }
  }

  return (
    <>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        data-testid="bird-photo-input"
        onChange={(event) => void handleChange(event)}
      />

      <button
        type="button"
        aria-label={label}
        disabled={upload.isPending}
        onClick={() => input.current?.click()}
        className="flex size-touch items-center justify-center rounded-full bg-surface-container-lowest/90 text-on-surface shadow-card disabled:opacity-60"
      >
        {upload.isPending ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <Camera className="size-5" />
        )}
      </button>
    </>
  );
}
